import type { GetOrganizationResponseBodyType } from "@intelligems/ig-types/functions/organizations/get-organization";
import {
  LoginResponseBodyType,
  LoginResponseBodyUserType,
} from "@intelligems/ig-types/functions/user-auths/login";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { organizationsKeys } from "@src/api/queryKeys";

import { AI_ROUTES } from "../../routes/routes";
import { IgStorage } from "../../utils/igStorage";
import { api } from "../api";
import {
  currentAppPath,
  leaveForLoginExit,
} from "../user-auths/leave-for-login-exit";

export const switchOrganizationByAgenticLogicRule = async (
  token: string,
  agenticLogicRuleId: string
): Promise<LoginResponseBodyType> => {
  const resp = await api.post<LoginResponseBodyType>("/v2/login", {
    token,
    agenticLogicRuleId,
    returnUrl: currentAppPath(),
  });
  return resp.data;
};

export const getPostAgenticLogicRuleSwitchAction = (
  requestedRule: LoginResponseBodyUserType["requestedAgenticLogicRule"]
): "openAgenticLogic" | "reloadCurrentRoute" =>
  requestedRule?.isDeleted ? "openAgenticLogic" : "reloadCurrentRoute";

/**
 * Moves the session into the org that owns a rule and reloads if it changed.
 *
 * The experience twin of this hook clears the redux slices and the query cache
 * by hand because it navigates in-app. Here an org change leaves through
 * the browser: the rule link can be opened from anywhere in the admin, and a
 * full document load is the only thing that rebuilds every org-scoped store —
 * redux, Apollo and react-query alike — from the new token. It also keeps this
 * hook free of Router and redux context, which matters because it is called
 * from `useAgenticLogicRule`, i.e. from every consumer of a rule.
 */
export const useSwitchOrganizationByAgenticLogicRule = () => {
  const queryClient = useQueryClient();
  return useMutation<
    LoginResponseBodyType,
    Error,
    { token: string; agenticLogicRuleId: string }
  >({
    mutationKey: [{ scope: "user" }],
    mutationFn: async (body) =>
      switchOrganizationByAgenticLogicRule(body.token, body.agenticLogicRuleId),
    onSuccess: async (data) => {
      if (data.type !== "login") {
        return leaveForLoginExit(data);
      }
      // eslint-disable-next-line react-compiler/react-compiler
      IgStorage.authToken = data.token;

      if (
        getPostAgenticLogicRuleSwitchAction(data.requestedAgenticLogicRule) ===
        "reloadCurrentRoute"
      ) {
        const currentOrg =
          queryClient.getQueryData<GetOrganizationResponseBodyType>(
            organizationsKeys.one
          );
        // A disabled feature also returns 404. Repeatedly logging into the
        // same org cannot enable it, so let the route show No Access.
        if (data.organization.id !== currentOrg?.org.id) {
          window.location.reload();
        }
        return;
      }

      // The rule is gone, so the route that asked for it no longer resolves.
      window.location.assign(AI_ROUTES.AiAgenticLogic);
    },
  });
};
